import React, { FormEvent, useCallback, useRef, useState } from "react";

// * Store
import {
    TranscodePreset,
    useGetUserQuery,
    useUpdateChannelMutation,
    useCreateTelegramChatLinkMutation,
    useDeleteTelegramConnectMutation,
    useGetChannelsQuery
} from "../../features/api/apiSlice";

// * Components
import { Button } from "../Button";
import { ButtonGroup } from "../ButtonGroup";
import { InputField } from "../InputField";
import { PasswordField } from "../PasswordField";
import { Popup } from "../Popup";
import { PopupTitle } from "../PopupTitle";
import { Error } from "../Error";
import PopupStructure from "../PopupStructure";
import InputLegend from "../InputLegend";
import ChatManagementPopup from "../AddChannelPopup/ChatManagementPopup/ChatManagementPopup";
import PopupTelegramChat from "../PopupTelegramChat/PopupTelegramChat";
import TextAreaField from "../TextAreaField";
import { PopupInfo } from "../PopupInfo/PopupInfo";
import TranscodePresetsPopup from "../TranscodePresetsPopup";

// * Hooks
import { useNavigate } from "react-router-dom";

// * Local
import { EditChannelPopupProps } from './EditChannelPopup.interface';
import s from "./EditChannelPopup.module.css";
import { mapStreamErrorMessages } from "../../helpers/mapStreamErrorMessages";

const EMPTY_REQUIRED_FIELD_ERROR_MESSAGE = 'Пустое поле';
const EMPTY_REQUIRED_FIELDS_ERROR_MESSAGE = 'Заполните обязательные поля';
const INVALID_TARGET_URL_ERROR_MESSAGE = 'Неверная ссылка на канал';
const INVALID_KEY_ERROR_MESSAGE = 'Неверный URL канала';
const NO_CHANNEL_INFO_ERROR_MESSAGE = 'Данный канал не найден';

function getDefaultErrors() {
    return { name: '', url: '', key: '', channelLink: '' };
}
const MAX_NAME_LENGTH = 100;

export const EditChannelPopup: React.FC<EditChannelPopupProps> = ({ channel, studio, ...popupProps }) => {
    const { data: user } = useGetUserQuery();
    const { data: channels, refetch } = useGetChannelsQuery();
    const [updateChannel] = useUpdateChannelMutation();
    const [createTelegramChatLink] = useCreateTelegramChatLinkMutation();
    const [deleteTelegramConnect] = useDeleteTelegramConnectMutation();

    const navigate = useNavigate();
    const nameRef = useRef<HTMLTextAreaElement>(null);
    const urlRef = useRef<HTMLInputElement>(null);
    const keyRef = useRef<HTMLInputElement>(null);
    const channelLinkRef = useRef<HTMLInputElement>(null);
    const currentChannel = channels?.restream_templates.find(c => c.id === channel.id);
    const displayedChannel = currentChannel || channel;
    const isTelegramPlatform = displayedChannel.platform === 'telegram';
    const [errorFields, setErrorFields] = useState(getDefaultErrors());
    const [transcodePreset, setTranscodePreset] = useState<TranscodePreset | undefined>(channel?.transcode_preset);
    const [isShowTranscodePresets, setIsShowTranscodePresets] = useState(false);
    const [nameLength, setNameLength] = useState(displayedChannel.name?.length || 0);
    const [name, setName] = useState(channel.name);
    const [errorText, setErrorText] = useState('');
    const [showWarning, setShowWarning] = useState(false);
    const [isChatManagementOpen, setIsChatManagementOpen] = useState(false);
    const [botCommand, setBotCommand] = useState<string>('');
    const [inviteUrl, setInviteUrl] = useState<string>('');

    const handleInput = useCallback(() => {
        setErrorFields(getDefaultErrors());
        setErrorText('');
    }, []);

    const checkActiveChannelChanges = () => {
        if (!channel.enabled || !channel.target_url || !channel.key) {
            return false;
        }

        const currentData = {
            key: keyRef.current?.value,
            url: urlRef.current?.value,
        };

        const initialData = {
            key: channel.key,
            url: channel.target_url
        };

        return currentData.key !== initialData.key || currentData.url !== initialData.url;
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value;

        setNameLength(e.target.value.length);
        setName(newValue);
    };

    const handleUpdateChannel = async (name: string, url: string, key: string, channelLink?: string) => {
        try {
            await updateChannel({
                id: channel.id,
                key,
                name,
                target_url: url,
                transcode_preset_id: transcodePreset?.id ?? 0,
                platform: displayedChannel.platform,
                channel_link: channelLink
            }).unwrap();

            await refetch();
            popupProps.onClose();
        } catch (error: any) {
            const errors = error.data?.errors || [];
            const errorMessage = errors.length === 1
                ? mapStreamErrorMessages(errors)
                : 'Данные некорректны';

            setErrorText(errorMessage);
            setErrorFields(prev => ({
                ...prev,
                url: errors.includes('INVALID_TARGET_URL') ? INVALID_TARGET_URL_ERROR_MESSAGE : '',
                key: errors.includes('INVALID_KEY') ? INVALID_KEY_ERROR_MESSAGE : '',
                channelLink: errors.includes('NO_CHANNEL_INFO') ? NO_CHANNEL_INFO_ERROR_MESSAGE : ''
            }));
        }
    };

    const handleTelegramChatClose = () => {
        refetch();
        setIsChatManagementOpen(false);
    };

    const handleChatManagement = (e: React.MouseEvent) => {
        e.preventDefault();

        if (displayedChannel.telegram_chat) {
            setIsChatManagementOpen(true);
            return;
        }

        createTelegramChatLink({ channelId: displayedChannel.id })
            .unwrap()
            .then(response => {
                setBotCommand(response.bot_command);
                setInviteUrl(response.invite_url);
                setIsChatManagementOpen(true);
            })
            .catch(error => {
                console.error('Failed to create chat link:', error);
                setErrorText('Не удалось создать ссылку для чата. Попробуйте позже.');
            });
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const name = nameRef.current?.value?.trim() || "";
        const url = urlRef.current?.value?.trim() || '';
        const key = keyRef.current?.value?.trim() || '';
        const channelLink = channelLinkRef?.current?.value?.trim() || '';

        const isNameEmpty = !name;
        const isUrlEmpty = !url;
        const isKeyEmpty = !key;
        const isChannelLinkEmpty = !channelLink;

        if (isNameEmpty || isUrlEmpty || isKeyEmpty || isChannelLinkEmpty) {
            let errors = getDefaultErrors();
            if (isNameEmpty || isUrlEmpty || isKeyEmpty) {
                errors = {
                    ...errors,
                    name: isNameEmpty ? EMPTY_REQUIRED_FIELD_ERROR_MESSAGE : '',
                    url: isUrlEmpty ? EMPTY_REQUIRED_FIELD_ERROR_MESSAGE : '',
                    key: isKeyEmpty ? EMPTY_REQUIRED_FIELD_ERROR_MESSAGE : ''
                };
            }
            if (channel.platform === "rutube" && isChannelLinkEmpty) {
                errors = {
                    ...errors,
                    channelLink: isChannelLinkEmpty ? EMPTY_REQUIRED_FIELD_ERROR_MESSAGE : ''
                };
            }
            if (Object.values(errors).some(errorMessage => errorMessage)) {
                setErrorFields(errors);
                return setErrorText(EMPTY_REQUIRED_FIELDS_ERROR_MESSAGE);
            }
        }

        if (channelLinkRef.current) {
            const {
                value: channelLinkValue,
                pattern: channelLinkPattern
            } = channelLinkRef.current;

            if (channelLinkValue && channelLinkPattern && !channelLinkValue.match(channelLinkPattern)) {
                setErrorFields((errors) => ({
                    ...errors,
                    channelLink: INVALID_TARGET_URL_ERROR_MESSAGE
                }));
                setErrorText(INVALID_TARGET_URL_ERROR_MESSAGE);
                return;
            }
        }

        if (checkActiveChannelChanges()) {
            setShowWarning(true);
            return;
        }

        handleUpdateChannel(name, url, key, channelLink);
    };

    const handleOpenManagingTranscodePresetsPopup = () => {
        if (!user?.tariff.max_active_transcodes) {
            navigate("/tariffs");
        }

        setIsShowTranscodePresets(true);
    };

    const getManagingTranscodePresetButtonLabel = () => {
        if (transcodePreset) {
            return transcodePreset.name;
        } else {
            return "Выбор пресета";
        }
    };

    const handleDisconnect = async () => {
        try {
            await deleteTelegramConnect(channel.id).unwrap();
            await refetch();

            const response = await createTelegramChatLink({ channelId: channel.id }).unwrap();

            setBotCommand(response.bot_command);
            setInviteUrl(response.invite_url);

            setIsChatManagementOpen(false);
            requestAnimationFrame(() => {
                setIsChatManagementOpen(true);
            });
        } catch (error) {
            console.error('Failed to disconnect telegram chat:', error);
            setErrorText('Не удалось отключить Telegram чат. Попробуйте позже.');
        }
    };

    let chatManagementPopup = null;
    if (isChatManagementOpen) {
        chatManagementPopup = displayedChannel.telegram_chat
            ? (
                <PopupTelegramChat
                    channelId={displayedChannel.id}
                    onClose={handleTelegramChatClose}
                    telegramChat={displayedChannel.telegram_chat}
                    onDisconnect={handleDisconnect}
                />
            ) : (
                <ChatManagementPopup
                    botCommand={botCommand}
                    inviteUrl={inviteUrl}
                    onClose={handleTelegramChatClose}
                    channel={displayedChannel}
                />
            );
    }

    return (
        <>
            <Popup {...popupProps} maxWidth="530px">
                <PopupStructure
                    headerComponent={
                        <PopupTitle onClose={popupProps.onClose}>
                            Редактировать канал
                        </PopupTitle>
                    }
                    contentComponent={
                        <React.Fragment>
                            <form id="channel-form" onSubmit={handleSubmit} className={s['form']}>
                                <InputLegend legend="Название канала">
                                    <TextAreaField
                                        isError={Boolean(errorFields.name)}
                                        maxLength={MAX_NAME_LENGTH}
                                        placeholder="Название*"
                                        onInput={handleInput}
                                        ref={nameRef}
                                        defaultValue={displayedChannel.name}
                                        onChange={handleNameChange}
                                        usedChars={nameLength}
                                        value={name}
                                        {...(channel.platform === "rutube" && {
                                            disabled: true,
                                            maxLength: undefined,
                                            style: {
                                                resize: "none"
                                            },
                                            rows: 1
                                        })}
                                    />
                                </InputLegend>

                                {channel.platform === "rutube" && (
                                    <InputLegend legend='Ссылка на канал'>
                                        <InputField
                                            isError={Boolean(errorFields.channelLink)}
                                            defaultValue={channel.channel_link}
                                            placeholder="https://rutube.ru/channel/example_id/"
                                            autoComplete="section-channel-link"
                                            ref={channelLinkRef}
                                            onInput={handleInput}
                                            pattern="^https:\/\/rutube\.ru\/channel\/[0-9_-]+\/$"
                                        />
                                    </InputLegend>
                                )}

                                <InputLegend legend="URL канала">
                                    <InputField
                                        isError={Boolean(errorFields.url)}
                                        placeholder="rtmp://example.com/live*"
                                        onInput={handleInput}
                                        ref={urlRef}
                                        defaultValue={displayedChannel.target_url}
                                    />
                                </InputLegend>

                                <InputLegend legend="Ключ канала">
                                    <PasswordField
                                        isError={Boolean(errorFields.key)}
                                        placeholder="Ключ*"
                                        onInput={handleInput}
                                        ref={keyRef}
                                        icon="toggle"
                                        defaultValue={displayedChannel.key}
                                    />
                                </InputLegend>

                                {Boolean(user?.tariff.max_active_transcodes) && !studio &&
                                    <Button
                                        kind="primary"
                                        type="button"
                                        onClick={handleOpenManagingTranscodePresetsPopup}
                                    >
                                        {getManagingTranscodePresetButtonLabel()}
                                    </Button>
                                }

                                {isTelegramPlatform && (
                                    <Button
                                        kind="primary"
                                        className={s["chat-management"]}
                                        onClick={handleChatManagement}
                                        type="button"
                                    >
                                        {displayedChannel.telegram_chat ? 'Настройки чата' : 'Управление чатом'}
                                    </Button>
                                )}
                            </form>
                            <Error className={s['error']}>{errorText}</Error>

                            {isShowTranscodePresets &&
                                <TranscodePresetsPopup
                                    isShow={isShowTranscodePresets}
                                    onClose={() => setIsShowTranscodePresets(false)}
                                    maxWidth="1000px"
                                    handleSelectPreset={setTranscodePreset}
                                    selectedTranscodePreset={transcodePreset}
                                />
                            }
                        </React.Fragment>
                    }
                    footerComponent={
                        <ButtonGroup>
                            <Button kind="secondary" type="button" onClick={popupProps.onClose}>
                                Отмена
                            </Button>
                            <Button form="channel-form" kind="primary" type="submit">
                                Сохранить
                            </Button>
                        </ButtonGroup>
                    }
                />
            </Popup>

            {showWarning && (
                <PopupInfo
                    title="Внимание"
                    message="Вы пытаетесь изменить настройки запущенного канала. Это может привести к перезапуску трансляции. Продолжить?"
                    confirmButtonText="Продолжить"
                    cancelButtonText="Отмена"
                    onConfirm={() => {
                        const name = nameRef.current?.value?.trim() || '';
                        const url = urlRef.current?.value?.trim() || '';
                        const key = keyRef.current?.value?.trim() || '';
                        const channelLink = channelLinkRef.current?.value?.trim() || '';

                        handleUpdateChannel(name, url, key, channelLink);
                        setShowWarning(false);
                    }}
                    onCancel={() => setShowWarning(false)}
                />
            )}

            {chatManagementPopup}
        </>
    );
};

export default EditChannelPopup;